import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, X } from "lucide-react";

const ALL = "__all__";

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [facets, setFacets] = useState({ users: [], actions: [], entities: [] });
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ user_id: ALL, action: ALL, entity: ALL, date_from: "", date_to: "", q: "" });

  // Facets load once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/audit-logs/facets");
        setFacets(data);
      } catch (e) { console.warn("failed to load audit facets", e); }
    })();
  }, []);

  // Re-fetch whenever filters change (debounced for the free-text field only)
  useEffect(() => {
    const controller = new AbortController();
    const params = {};
    if (f.user_id !== ALL) params.user_id = f.user_id;
    if (f.action !== ALL) params.action = f.action;
    if (f.entity !== ALL) params.entity = f.entity;
    if (f.date_from) params.date_from = new Date(f.date_from).toISOString();
    if (f.date_to)   params.date_to   = new Date(f.date_to + "T23:59:59").toISOString();
    if (f.q.trim())  params.q = f.q.trim();
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/audit-logs", { params, signal: controller.signal });
        setLogs(data);
      } catch (e) {
        if (e.name !== "CanceledError") console.warn(e);
      }
      finally { setLoading(false); }
    }, f.q ? 250 : 0);
    return () => { clearTimeout(t); controller.abort(); };
  }, [f]);

  const isFiltered = useMemo(() => (
    f.user_id !== ALL || f.action !== ALL || f.entity !== ALL || f.date_from || f.date_to || f.q
  ), [f]);
  const reset = () => setF({ user_id: ALL, action: ALL, entity: ALL, date_from: "", date_to: "", q: "" });

  return (
    <div className="space-y-8" data-testid="audit-page">
      <div>
        <div className="overline">Compliance</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Audit log</h1>
      </div>

      {/* Filters */}
      <div className="grid-border-card p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="overline block mb-1.5">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"/>
              <Input
                value={f.q}
                onChange={e => setF({...f, q: e.target.value})}
                placeholder="Entity ID or note fragment"
                className="pl-9"
                data-testid="audit-search"
              />
            </div>
          </div>
          <div className="w-full sm:w-48">
            <label className="overline block mb-1.5">User</label>
            <Select value={f.user_id} onValueChange={v => setF({...f, user_id: v})}>
              <SelectTrigger data-testid="audit-user-filter"><SelectValue placeholder="All users"/></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All users</SelectItem>
                {facets.users.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full sm:w-44">
            <label className="overline block mb-1.5">Action</label>
            <Select value={f.action} onValueChange={v => setF({...f, action: v})}>
              <SelectTrigger data-testid="audit-action-filter"><SelectValue placeholder="All actions"/></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All actions</SelectItem>
                {facets.actions.map(a => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full sm:w-40">
            <label className="overline block mb-1.5">Entity</label>
            <Select value={f.entity} onValueChange={v => setF({...f, entity: v})}>
              <SelectTrigger data-testid="audit-entity-filter"><SelectValue placeholder="All entities"/></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All entities</SelectItem>
                {facets.entities.map(e => (
                  <SelectItem key={e} value={e}>{e}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="overline block mb-1.5">From</label>
            <Input type="date" value={f.date_from} onChange={e => setF({...f, date_from: e.target.value})} data-testid="audit-date-from"/>
          </div>
          <div>
            <label className="overline block mb-1.5">To</label>
            <Input type="date" value={f.date_to} onChange={e => setF({...f, date_to: e.target.value})} data-testid="audit-date-to"/>
          </div>
          {isFiltered && (
            <Button variant="outline" onClick={reset} data-testid="audit-reset-btn" className="gap-2">
              <X className="w-4 h-4"/>Reset
            </Button>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-3">
          {loading ? "Loading…" : `${logs.length} entr${logs.length === 1 ? "y" : "ies"}`}
          {isFiltered && !loading && <span> · filtered</span>}
        </div>
      </div>

      <div className="grid-border-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Action</th>
                <th className="text-left px-4 py-3">Entity</th>
                <th className="text-left px-4 py-3">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {logs.map(l => (
                <tr key={l.id} className="hover:bg-secondary/40" data-testid="audit-row">
                  <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3">{l.user_name}</td>
                  <td className="px-4 py-3"><span className="px-2 py-0.5 rounded bg-secondary text-xs">{l.action}</span></td>
                  <td className="px-4 py-3 text-muted-foreground">{l.entity}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground truncate max-w-[400px]">{JSON.stringify(l.details)}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">
                  {isFiltered ? "No log entries match those filters" : "No log entries yet"}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
